import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Search, FileText } from "lucide-react";
import { toast } from "sonner";
import FileAttachmentField, { AttachmentBadge } from "@/components/FileAttachmentField";
import ExportButton from "@/components/ExportButton";

const STATUSES = ["ACTIVE","UNDER_REVIEW","EXPIRED","DRAFT"];
const TYPES = ["Manual","SOP","Work Instruction","Form","Kebijakan"];

const statusBadge = (s) => {
  const map = {
    ACTIVE: "bg-emerald-100 text-emerald-700",
    UNDER_REVIEW: "bg-amber-100 text-amber-700",
    EXPIRED: "bg-red-100 text-red-700",
    DRAFT: "bg-slate-100 text-slate-600",
  };
  return <Badge className={`${map[s] || map.DRAFT} hover:${map[s]}`}>{s}</Badge>;
};

const levelTitles = { 1: "Dokumen Level 1", 2: "Dokumen Level 2", 3: "Dokumen Level 3", 4: "Dokumen Level 4" };

export default function DocumentsPage({ level }) {
  const qc = useQueryClient();
  const { canWrite } = useAuth();
  const canEdit = canWrite("document");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [type, setType] = useState("ALL");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const { data = [], isLoading } = useQuery({
    queryKey: ["documents"],
    queryFn: async () => (await apiClient.get("/documents")).data,
  });

  const rows = useMemo(() => data.filter(d => d.level === level), [data, level]);
  const filtered = useMemo(() => rows.filter(d => {
    const matchQ = !q || d.document_number?.toLowerCase().includes(q.toLowerCase()) || d.title?.toLowerCase().includes(q.toLowerCase());
    const matchS = status === "ALL" || d.status === status;
    const matchT = type === "ALL" || d.document_type === type;
    return matchQ && matchS && matchT;
  }), [rows, q, status, type]);

  const summary = useMemo(() => ({
    total: rows.length,
    active: rows.filter(r => r.status === "ACTIVE").length,
    expired: rows.filter(r => r.status === "EXPIRED").length,
    review: rows.filter(r => r.status === "UNDER_REVIEW").length,
  }), [rows]);

  const mSave = useMutation({
    mutationFn: async (b) => b.id ? (await apiClient.put(`/documents/${b.id}`, b)).data : (await apiClient.post("/documents", b)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:["documents"]}); qc.invalidateQueries({queryKey:["dashboard-stats"]}); toast.success("Dokumen disimpan"); setOpen(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal menyimpan"),
  });
  const mDel = useMutation({
    mutationFn: async (id) => (await apiClient.delete(`/documents/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:["documents"]}); qc.invalidateQueries({queryKey:["dashboard-stats"]}); toast.success("Dokumen dihapus"); },
  });

  const openNew = () => { setEditing({ document_number:"", title:"", document_type: TYPES[0], revision:"Rev 1", effective_date:"", expiry_date:"", owner:"", status:"ACTIVE", level, notes:"", attachment: null }); setOpen(true); };

  const exportCols = [
    { key:"document_number", label:"Nomor" },{ key:"title", label:"Judul" },{ key:"document_type", label:"Tipe" },
    { key:"revision", label:"Revisi" },{ key:"effective_date", label:"Efektif" },{ key:"expiry_date", label:"Berakhir" },
    { key:"owner", label:"Owner" },{ key:"status", label:"Status" },
  ];

  return (
    <div className="space-y-6" data-testid={`docs-l${level}-page`}>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#00A651]">Dokumen JKTOFQG</div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>{levelTitles[level]}</h1>
        <p className="text-sm text-slate-500 mt-1">Repositori dokumen operasional divisi.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ["Total Dokumen", summary.total, "bg-slate-100 text-slate-700"],
          ["Aktif", summary.active, "bg-emerald-100 text-emerald-700"],
          ["Review", summary.review, "bg-amber-100 text-amber-700"],
          ["Kadaluarsa", summary.expired, "bg-red-100 text-red-700"],
        ].map(([l,v,t]) => (
          <Card key={l} className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t}`}><FileText className="w-5 h-5"/></div>
            <div><div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{l}</div>
            <div className="text-xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>{v}</div></div>
          </CardContent></Card>
        ))}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between">
            <div className="flex flex-col sm:flex-row gap-2 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Cari nomor / judul dokumen..." className="pl-9" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="docs-search"/>
              </div>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="w-full sm:w-44"><SelectValue placeholder="Tipe"/></SelectTrigger>
                <SelectContent><SelectItem value="ALL">Semua Tipe</SelectItem>{TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-full sm:w-44"><SelectValue placeholder="Status"/></SelectTrigger>
                <SelectContent><SelectItem value="ALL">Semua Status</SelectItem>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {canEdit && <Button onClick={openNew} className="bg-[#00A651] hover:bg-[#008741]" data-testid="docs-add-btn"><Plus className="w-4 h-4 mr-1"/> Tambah Dokumen</Button>}
            <ExportButton rows={filtered} columns={exportCols} title={`${levelTitles[level]}`} filename={`dokumen-l${level}`} testid="docs-export-btn"/>
          </div>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table className="fodm-table">
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Nomor</TableHead><TableHead>Judul</TableHead><TableHead>Tipe</TableHead>
                  <TableHead>Revisi</TableHead><TableHead>Efektif</TableHead><TableHead>Berakhir</TableHead>
                  <TableHead>Owner</TableHead><TableHead>Status</TableHead><TableHead>File</TableHead>{canEdit && <TableHead className="text-right">Aksi</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? Array.from({length:4}).map((_,i)=>(<TableRow key={i}><TableCell colSpan={canEdit?10:9}><Skeleton className="h-6 w-full"/></TableCell></TableRow>))
                : filtered.length === 0 ? <TableRow><TableCell colSpan={canEdit?10:9} className="text-center py-8 italic text-slate-500 text-sm">Tidak ada dokumen.</TableCell></TableRow>
                : filtered.map(d => (
                  <TableRow key={d.id} data-testid="doc-row">
                    <TableCell className="font-mono text-xs text-slate-700">{d.document_number}</TableCell>
                    <TableCell className="font-medium text-slate-800 max-w-xs truncate">{d.title}</TableCell>
                    <TableCell><Badge variant="outline">{d.document_type}</Badge></TableCell>
                    <TableCell className="text-xs">{d.revision}</TableCell>
                    <TableCell className="text-xs text-slate-500">{d.effective_date || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{d.expiry_date || "—"}</TableCell>
                    <TableCell className="text-xs">{d.owner}</TableCell>
                    <TableCell>{statusBadge(d.status)}</TableCell>
                    <TableCell><AttachmentBadge path={d.attachment?.path} name={d.attachment?.name}/></TableCell>
                    {canEdit && <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={()=>{setEditing({...d}); setOpen(true);}}><Pencil className="w-4 h-4"/></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-red-600"><Trash2 className="w-4 h-4"/></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Hapus dokumen?</AlertDialogTitle><AlertDialogDescription>Dokumen <b>{d.document_number}</b> akan dihapus.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={()=>mDel.mutate(d.id)}>Hapus</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Ubah" : "Tambah"} Dokumen</DialogTitle></DialogHeader>
          {editing && <div className="grid grid-cols-2 gap-3">
            <div><Label>Nomor Dokumen</Label><Input value={editing.document_number} onChange={(e)=>setEditing({...editing, document_number:e.target.value})}/></div>
            <div><Label>Revisi</Label><Input value={editing.revision} onChange={(e)=>setEditing({...editing, revision:e.target.value})}/></div>
            <div className="col-span-2"><Label>Judul</Label><Input value={editing.title} onChange={(e)=>setEditing({...editing, title:e.target.value})}/></div>
            <div><Label>Tipe</Label>
              <Select value={editing.document_type} onValueChange={(v)=>setEditing({...editing, document_type:v})}><SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Status</Label>
              <Select value={editing.status} onValueChange={(v)=>setEditing({...editing, status:v})}><SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Tgl Efektif</Label><Input type="date" value={editing.effective_date || ""} onChange={(e)=>setEditing({...editing, effective_date:e.target.value})}/></div>
            <div><Label>Tgl Berakhir</Label><Input type="date" value={editing.expiry_date || ""} onChange={(e)=>setEditing({...editing, expiry_date:e.target.value})}/></div>
            <div className="col-span-2"><Label>Owner / Unit</Label><Input value={editing.owner || ""} onChange={(e)=>setEditing({...editing, owner:e.target.value})}/></div>
            <div className="col-span-2"><Label>Lampiran File</Label>
              <FileAttachmentField value={editing.attachment && typeof editing.attachment === "object" ? editing.attachment : null} onChange={(v)=>setEditing({...editing, attachment: v})} testid="docs-upload"/>
            </div>
            <div className="col-span-2"><Label>Catatan</Label><Input value={editing.notes || ""} onChange={(e)=>setEditing({...editing, notes:e.target.value})}/></div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button className="bg-[#00A651] hover:bg-[#008741]" onClick={()=>mSave.mutate(editing)} data-testid="docs-form-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
